import { Injectable } from '@angular/core';
import { OpenEstimateApiService } from '../api/open-estimate.service';
import { take } from 'rxjs';
import { CONSTANTS } from '../../Constants/Constants';
import { DropDownItem } from '../../models/dtos/Classes/DropDownItem';

import {
  BusinessAndSalesOffice, BusinessHierarchy, CostReviewDatabaseRecord, CollaborationData,
  DeploymentOfficeWithLaborRatesResponse,
  EstimateData, EstimateMetaData, EstimateSettings,
  InitializeEstimatePropertiesDropDownDataRequest, InitializeEstimatePropertiesDropDownDataResponse,
  LaborMetaData, LaborRates, OpenEstimateResponse, OtherMetaData,
  CollaborationState,
  CollaborationVerificationResult,
  OverTimeRates,
  OverTimeAttributes,
  TaskBasedLaborSettings
} from '../../models/dtos';
import { OvertimePeriodKey } from '../../models/properties/OvertimeRate';
import { CommonDataService } from '../common-data.service';
import { CollaborationOperationsService } from '../common/collaboration-operations.service';
import { CollaboratorDetails } from '../../models/dtos/Classes/CollaboratorDetails';
import { EstimateSaveService } from '../estimate-save.service';
import { BehaviorSubject, Observable } from "rxjs";
import { NotificationMessageService } from '../common/notification-message.service';
import { CommonMetaDataService } from '../common-meta-data.service';
import { StaticDropdownService } from '../common/static-dropdown.service';
import { EngineeringOfficesWithLaborRatesResponse } from '../../models/dtos/Classes/EngineeringOfficesWithLaborRatesResponse';
import { EscalationResetService } from '../operations/escalation-reset.service';
import { ProjectManagementHoursUpdateService } from '../labor/project-management-hours-update.service';
import { LoadingSpinnerService } from '../common/loading-spinner.service';
import { TranslateService } from '@ngx-translate/core';
import { CommonFunctionsService } from '../common-functions.service';

@Injectable()
export class SolPropertiesService {
  private estimatePropertiesMasterData: InitializeEstimatePropertiesDropDownDataResponse;
  private openEstimateResponse: OpenEstimateResponse;
  private estimateData: EstimateData;
  private estimateMetaData: EstimateMetaData;
  private otherMetaData: OtherMetaData;
  private laborMetaData: LaborMetaData;
  isLoading: boolean = true;
  private _initialCollaborationState: CollaborationState = CollaborationState.NonCollaborative;
  private _initialCollaborators: CollaboratorDetails[] = [];
  private _initialProjectStartDate: Date | null = null;
  private _initialProjectEndDate: Date | null = null;
  private _escalationResetPending: boolean = false;
  private _propertiesChanged: boolean = false;
  private _otherPropertiesChanged: boolean = false;
  private _collaboratorList: CollaboratorDetails[] = [];
  private collaborationStateReverted = new BehaviorSubject<boolean>(false);
  collaborationStateReverted$ = this.collaborationStateReverted.asObservable();
  overtimeRatesFormState: { salesOffice: number | null } | null = null;

  setCollaboratorList(list: CollaboratorDetails[]): void {
    this._collaboratorList = list;
  }

  getCollaboratorList(): CollaboratorDetails[] {
    return this._collaboratorList;
  }

  constructor(
    private readonly estimateApiService: OpenEstimateApiService,
    private readonly commonDataService: CommonDataService,
    private readonly collaborationOperationsService: CollaborationOperationsService,
    private readonly estimateSaveService: EstimateSaveService,
    private readonly notificationMessageService: NotificationMessageService,
    private readonly commonMetaDataService: CommonMetaDataService,
    private readonly staticDropDownService: StaticDropdownService,
    private readonly escalationResetService: EscalationResetService,
    private readonly projectManagementHoursUpdateService: ProjectManagementHoursUpdateService,
    private readonly loadingSpinnerService: LoadingSpinnerService,
    private readonly translateService: TranslateService,
    private readonly commonFunctionsService: CommonFunctionsService
  ) { }

  // TODO: This is just a sample method to call all the necessary APIs for estimate properties in parallel. 
  // Later will replaced with actual API calls flow.
  callEstimateApis(): void {
    this.initializePropertiesData();
    let estimateMetaData = this.estimateMetaData;
    let estimateData = this.estimateData;

    let propertiesRequest: InitializeEstimatePropertiesDropDownDataRequest = {
      salesOfficeId: estimateMetaData.estimateSettings.salesOfficeId,
      division: estimateData.division,
      businessUnitId: estimateMetaData.estimateSettings.businessUnitId,
      business: estimateData.business,
      cultureId: estimateData.cultureID,
      segmentId: estimateData.profitCenterId,
      subSegmentID: estimateData.subSegmentID,
      isOldSapSubmitted: false,
      isECSApplicable: estimateData.isECS ?? false
    };
    this.estimateApiService.initializePropertiesDropDowns(propertiesRequest)
      .pipe(take(CONSTANTS.ONE))
      .subscribe((estimateProperties) => {
        this.estimatePropertiesMasterData = estimateProperties;

        this.isLoading = false;
      });
  }

  getBusinessesAndSalesOfficesMasterData(): BusinessAndSalesOffice[] {
    return this.commonMetaDataService.getMetaData()?.businessesAndSalesOffices ?? [];
  }

  getEstimateTypesMasterData(): DropDownItem[] {
    return this.commonMetaDataService.getMetaData()?.estimateType ?? [];
  }

  getVerticalMarketsMasterData(): DropDownItem[] {
    return this.commonMetaDataService.getMetaData()?.verticalMarkets ?? [];
  }

  getUserTypesMasterData(): DropDownItem[] {
    return this.commonMetaDataService.getMetaData()?.userTypes ?? [];
  }

  getInstallTypesMasterData(): DropDownItem[] {
    return this.commonMetaDataService.getMetaData()?.installTypes ?? [];
  }

  getTaxTypesMasterData(): DropDownItem[] {
    return this.commonMetaDataService.getMetaData()?.taxTypes ?? [];
  }

  getRolesMasterData(): DropDownItem[] {
    return this.commonMetaDataService.getMetaData()?.collaboratorRoles ?? [];
  }

  getExcludedCollaboratorRoleIds(): number[] {
    return this.commonMetaDataService.getMetaData()?.collaboratorRoleIdsToExclude ?? [];
  }

  getProposalMaterialsMasterData(): Observable<DropDownItem[]> {
    return this.staticDropDownService.getPropertiesMaterials();
  }

  getProposalLaborMasterData(): Observable<DropDownItem[]> {
    return this.staticDropDownService.getLabors();
  }

  getProposalSubcontractorsMasterData(): Observable<DropDownItem[]> {
    return this.staticDropDownService.getSubcontractors();
  }

  getProposalMiscellaneousMasterData(): Observable<DropDownItem[]> {
    return this.staticDropDownService.getMiscellaneous();
  }

  getProposalTravelsMasterData(): Observable<DropDownItem[]> {
    return this.staticDropDownService.getTravels();
  }

  getPriceUnderCategoriesMasterData(): Observable<DropDownItem[]> {
    return this.staticDropDownService.getPriceUnderCategories();
  }

  getProposalMaterialListMasterData(): Observable<DropDownItem[]> {
    return this.staticDropDownService.getMaterialListingOptions();
  }

  getProposalMaterialListByAreaMasterData(): Observable<DropDownItem[]> {
    return this.staticDropDownService.getMaterialListingOptionsByArea();
  }

  getProposalIdentifierMasterData(): Observable<DropDownItem[]> {
    return this.staticDropDownService.getIdentifier();
  }

  getSegmentsMasterData(): DropDownItem[] {
    return this.estimatePropertiesMasterData.segments ?? [];
  }

  getBusinessHierarchiesMasterData(): BusinessHierarchy[] {
    return this.estimatePropertiesMasterData.businessHeirarchies ?? [];
  }

  getEngineeringOfficesMasterData(): EngineeringOfficesWithLaborRatesResponse[] {
    return this.estimatePropertiesMasterData.engineeringOfficesWithLaborRates ?? [];
  }

  getDeploymentOfficesMasterData(): DeploymentOfficeWithLaborRatesResponse[] {
    return this.estimatePropertiesMasterData.deploymentOffices ?? [];
  }

  getSegmentName(profitCenterId: number): string {
    const segment = this.commonMetaDataService.getMetaData()?.segments?.find(s => s.zweigId === profitCenterId);
    return segment ? segment.zweigName : CONSTANTS.EMPTY_STRING;
  }

  getInitialCollaborators(): CollaboratorDetails[] {
    return this._initialCollaborators;
  }

  setInitialCollaborators(collaborators: CollaboratorDetails[]): void {
    this._initialCollaborators = structuredClone(collaborators);
  }

  private initializePropertiesData(): void {
    this.openEstimateResponse = this.deepClone(this.commonDataService.getEstimateOpenResponse());
    this.estimateData = this.openEstimateResponse.estimateData;
    this.estimateMetaData = this.deepClone(this.commonDataService.getEstimateMetaData());
    this.otherMetaData = this.deepClone(this.commonDataService.getOtherMetaData());
    this.laborMetaData = this.deepClone(this.commonDataService.getLaborMetaData());
    this._initialCollaborationState = this.openEstimateResponse?.collaborationData?.collaborationState ?? CollaborationState.NonCollaborative;
    this._initialProjectStartDate = this.estimateData?.startDate ? new Date(this.estimateData.startDate) : null;
    this._initialProjectEndDate = this.estimateData?.endDate ? new Date(this.estimateData.endDate) : null;
    this._escalationResetPending = false;
    this._propertiesChanged = false;
    this._otherPropertiesChanged = false;
  }

  markPropertiesChanged(): void {
    this._propertiesChanged = true;
    this._otherPropertiesChanged = true;
  }

  /** Collaborator edits are persisted by the modify API, so they must not enable Save. */
  markCollaborationChanged(): void {
    this._propertiesChanged = true;
  }

  hasPropertiesChanged(): boolean {
    return this._propertiesChanged;
  }

  hasNonCollaborationChanges(): boolean {
    return this._otherPropertiesChanged;
  }

  getInitialCollaborationState(): CollaborationState {
    return this._initialCollaborationState;
  }

  isProposalReadOnly(isReadOnly: boolean): boolean {
    const state = this.getCollaborationData()?.collaborationState;
    return isReadOnly && (!this.commonDataService.isSapSubmitted
      || state === CollaborationState.Locked
      || state === CollaborationState.Open);
  }

  /**
   * Confirms and stages an escalation reset when the edited project dates moved outside the
   * range the current escalation spread was built for.
   *
   * @returns false when the user declines, meaning the save must be aborted.
   */
  async confirmEscalationResetForProjectDateChange(): Promise<boolean> {

    const isResetRequired = this.escalationResetService.isEscalationResetRequiredForDateChange(
      this._initialProjectStartDate,
      this._initialProjectEndDate,
      this.estimateData?.startDate ?? null,
      this.estimateData?.endDate ?? null
    );

    if (!isResetRequired) {
      this.discardStagedEscalationReset();
      return true;
    }

    const needsConfirmation = this.escalationResetService.isEscalationConfirmationRequired(
      this.estimateData?.startDate ?? null,
      this.estimateData?.endDate ?? null
    );

    if (needsConfirmation && !await this.escalationResetService.confirmEscalationResetForDateChange()) {
      this.estimateData.startDate = this._initialProjectStartDate;
      this.estimateData.endDate = this._initialProjectEndDate;
      return false;
    }

    this.escalationResetService.resetEscalationForDateChange(
      this.estimateMetaData.escalationRates,
      this.estimateData.startDate,
      this.estimateData.endDate
    );
    this._escalationResetPending = true;
    this.markPropertiesChanged();

    return true;
  }

  /**
   * Drops a reset staged by an earlier save attempt that never reached
   * savePropertiesToCommonDataService, so reverted project dates don't commit the stale spread.
   */
  private discardStagedEscalationReset(): void {
    if (!this._escalationResetPending) {
      return;
    }

    this._escalationResetPending = false;

    const committedEscalationRates = this.commonDataService.getEstimateMetaData()?.escalationRates;
    if (committedEscalationRates && this.estimateMetaData) {
      this.estimateMetaData.escalationRates = this.deepClone(committedEscalationRates);
    }
  }

  notifyNoCollaborators(): void {
    this.notificationMessageService.showError('COLLABORATION_MIN_COLLABORATOR_REQ_MESSAGE', "MISSING_COLLABORATORS_ERROR",);
  }


  async applyCollaborationChanges(): Promise<boolean> {
    const currentState = this.getCollaborationData().collaborationState;
    const wasEnabled = this._initialCollaborationState !== CollaborationState.NonCollaborative;
    const isNowEnabled = currentState !== CollaborationState.NonCollaborative;

    // No collaboration state change - nothing to apply
    if (!wasEnabled && !isNowEnabled) {
      return true;
    }

    if (!wasEnabled && isNowEnabled) {
      const estimateSaveRequest = await this.estimateSaveService.createSaveEstimateRequest();
      estimateSaveRequest.isCollaborativeEstimate = false;
      const result = await this.collaborationOperationsService.enableCollaboration(this._collaboratorList, estimateSaveRequest);
      if (result) {
        this.commonFunctionsService.updateCollaborationHubData(result);
        this.commonDataService.setCollaborationState(CollaborationState.Edit);
        this.commonDataService.setCollaboratorDataList(this._collaboratorList);
        this._initialCollaborationState = CollaborationState.Edit;
        this.commonDataService.enableReadOnlyMode(false);
        this.commonDataService.isSaveEnabled = false;
      } else if (!result && !wasEnabled && isNowEnabled) {
        this.collaborationStateReverted.next(true);
      }
      return !!result;
    } else if (wasEnabled && isNowEnabled) {
      const modifiedCollaborators = this.collaborationOperationsService.getModifiedCollaborators(this._initialCollaborators, this._collaboratorList);

      // If no collaborators were modified, nothing to apply
      const hasModifiedCollaborators = modifiedCollaborators &&
        (modifiedCollaborators.added?.length > CONSTANTS.ZERO ||
          modifiedCollaborators.removed?.length > CONSTANTS.ZERO ||
          modifiedCollaborators.altered?.length > CONSTANTS.ZERO || modifiedCollaborators.primaryUserChanged);

      if (!hasModifiedCollaborators) {
        return true;
      }

      const modificationNotes = this.collaborationOperationsService.getCollaborationModificationNotes(modifiedCollaborators);
      const result = await this.collaborationOperationsService.modifyCollaboration(this._collaboratorList, modificationNotes, modifiedCollaborators);
      if (result) {
        this.commonFunctionsService.updateCollaborationHubData(result);
        this.commonDataService.setCollaborationState(CollaborationState.Edit);
        this._initialCollaborationState = CollaborationState.Edit;
        this.commonDataService.setCollaboratorDataList(this._collaboratorList);
      }
      return !!result;
    } else {
      // wasEnabled && !isNowEnabled - Disabling collaboration
      const estimateSaveRequest = await this.estimateSaveService.createSaveEstimateRequest();
      const result = await this.collaborationOperationsService.disableCollaboration(this._collaboratorList, estimateSaveRequest);
      if (result) {
        this.commonDataService.setCollaborationData(this.getCollaborationData());
        this.commonDataService.setCollaborationState(CollaborationState.NonCollaborative);
        this._initialCollaborationState = CollaborationState.NonCollaborative;
        this.commonDataService.setCollaboratorDataList([]);
        this.commonDataService.isSaveEnabled = false;
      } else {
        this.collaborationStateReverted.next(true);
      }
      return result;
    }
  }

  getEstimateData(): EstimateData {
    return this.estimateData;
  }

  getEstimateSettings(): EstimateSettings {
    return this.estimateMetaData.estimateSettings;
  }

  getEstimateMetaDataModel(): EstimateMetaData {
    return this.estimateMetaData;
  }

  getOtherMetaDataModel(): OtherMetaData {
    return this.otherMetaData;
  }

  getSalesOfficeLaborRates(): LaborRates[] {
    return this.laborMetaData?.salesOfficeLaborRates ?? [];
  }

  getTaskBasedLaborSettings(): TaskBasedLaborSettings {
    return this.laborMetaData?.taskBasedLaborSettings;
  }

  private getStagedOvertimeRates(salesOfficeId: number): OverTimeRates | null {
    return this.getSalesOfficeLaborRates()
      .find(rate => rate.salesOfficeId === salesOfficeId)?.stdOverTimeRates ?? null;
  }

  isOvertimeOverrideEnabled(salesOfficeId: number): boolean {
    return this.getStagedOvertimeRates(salesOfficeId)?.isCalculationOverridden ?? false;
  }

  /**
   * Toggles the override flag for a sales office on the staged labor metadata.
   * Turning the override off restores the server-calculated overtime rates captured when the
   * estimate was opened, so the derived values reappear in place of the manual entries.
   */
  setOvertimeOverride(salesOfficeId: number, enabled: boolean): void {
    const stagedRates = this.getStagedOvertimeRates(salesOfficeId);
    if (!stagedRates) {
      return;
    }

    stagedRates.isCalculationOverridden = enabled;
    if (enabled) {
      return;
    }

    const defaultRates = this.commonDataService.getDefaultOvertimeRates(salesOfficeId);
    if (!defaultRates) {
      return;
    }

    stagedRates.overTime11X = this.deepClone(defaultRates.overTime11X);
    stagedRates.overTime15X = this.deepClone(defaultRates.overTime15X);
    stagedRates.overTime2X = this.deepClone(defaultRates.overTime2X);
  }

  updateOvertimeRate(salesOfficeId: number, period: OvertimePeriodKey, rateKey: keyof OverTimeAttributes, value: number): void {
    const stagedRates = this.getStagedOvertimeRates(salesOfficeId);
    if (!stagedRates?.[period]) {
      return;
    }
    stagedRates[period][rateKey] = Number.isFinite(value) ? Number(value) : CONSTANTS.ZERO;
  }

  /**
   * Commits the staged labor metadata (including overtime overrides) to the shared store.
   * Kept separate from savePropertiesToCommonDataService so it can also be triggered from an
   * unsaved-changes confirmation before navigating away from the properties panel.
   */
  private saveLaborMetaDataChanges(): void {
    if (!this.laborMetaData) {
      return;
    }
    // using latest task-based labor settings
    const liveTaskBasedLaborSettings = this.commonDataService.getTaskBasedLaborSettings();
    this.laborMetaData.taskBasedLaborSettings = this.deepClone(liveTaskBasedLaborSettings);
    this.commonDataService.patchLaborMetaData(this.deepClone(this.laborMetaData));
  }

  getCollaborationData(): CollaborationData {
    return this.openEstimateResponse.collaborationData;
  }

  getOpenEstimateResponse(): OpenEstimateResponse {
    return this.openEstimateResponse;
  }


  async savePropertiesToCommonDataService(): Promise<void> {
    this.loadingSpinnerService.show(CONSTANTS.DASHBOARD_SPINNER);
    this.loadingSpinnerService.updateMessage(this.translateService.instant('APPLYING_CHANGE'));
    // The cascade below is synchronous, so yield once to let the spinner paint first.
    await new Promise<void>(resolve => setTimeout(resolve));

    try {
      const previousGmPercent = this.commonDataService.getOtherMetaData()?.grossMargin?.soldASGMPercent ?? CONSTANTS.ZERO;
      // Read before publishing: publishing re-patches the estimate, which rewrites this value in Fixed Price.
      const propertiesGmPercent = this.otherMetaData?.grossMargin?.soldASGMPercent ?? CONSTANTS.ZERO;
      const previousProjectDates = this.commonDataService.projectStartDateEndDate;
      const editedEstimateResponse = this.deepClone(this.openEstimateResponse);
      // The project container is owned by the estimate store and keeps being updated while the
      // properties panel is open, so keep the live instance instead of the panel's snapshot.
      const liveProjectObject = this.commonDataService.getEstimateData()?.projectObject;
      editedEstimateResponse.estimateData.projectObject =
        liveProjectObject ?? editedEstimateResponse.estimateData.projectObject;

      // Merge panel edits into the live container BEFORE publishing so setOpenEstimate sees a
      // consistent tree and level-4 metadata setters aren't needed as follow-up writes.
      const projectObject = editedEstimateResponse.estimateData.projectObject;
      if (projectObject?.metaData) {
        projectObject.metaData.estimateMetaData = this.deepClone(this.estimateMetaData);
        projectObject.metaData.otherMetaData = this.deepClone(this.otherMetaData);
      }

      this.commonDataService.publishOpenEstimate(editedEstimateResponse);
      this.commonDataService.publishProjectContainer(projectObject);
      // Fire the change notification exactly once so the calculation cascade runs on save.
      this.commonDataService.patchOtherMetaData(projectObject?.metaData?.otherMetaData ?? this.deepClone(this.otherMetaData));
      const numericPropertiesGmPercent = Number(propertiesGmPercent);
        this.commonDataService.notifyPropertiesGmPercentChanged(
            numericPropertiesGmPercent,
            numericPropertiesGmPercent !== Number(previousGmPercent)
        );
      this.saveLaborMetaDataChanges();

      // Report header dates are shared state, so publish them only once the panel edits are committed.
      this.commonDataService.updateReportDates(
        editedEstimateResponse.estimateData.startDate,
        editedEstimateResponse.estimateData.endDate
      );

      // Project management hours are duration driven, so they are refreshed - and the cost cascade
      // re-run - before escalation is announced, keeping escalation on top of the updated costs.
      await this.projectManagementHoursUpdateService.updateHoursOnDurationChange(
        editedEstimateResponse.estimateData.startDate,
        editedEstimateResponse.estimateData.endDate,
        previousProjectDates
      );

      if (this._escalationResetPending) {
        this._escalationResetPending = false;
        this.escalationResetService.notifyEscalationReset();
      }
    } finally {
      this.loadingSpinnerService.updateMessage('');
      this.loadingSpinnerService.hide(CONSTANTS.DASHBOARD_SPINNER);
    }
  }

  private deepClone<T>(data: T): T {
    return data ? structuredClone(data) as T : data;
  }

  getVerifyCollaboratorsReasonTranslationKey(reason: number): string {
    let translatedText = CONSTANTS.EMPTY_STRING;

    switch (reason) {
      case CollaborationVerificationResult.CollaboratorNotFound:
        translatedText = 'COLLABORATOR_NOT_FOUND';
        break;
      case CollaborationVerificationResult.CountryCodeMismatch:
        translatedText = 'COUNTRY_CODE_MISMATCH';
        break;
      case CollaborationVerificationResult.InvalidEmailFormat:
        translatedText = 'INVALID_EMAIL_FORMAT';
        break;
      case CollaborationVerificationResult.GocRestricted:
        translatedText = 'GOC_RESTRICTED';
        break;
      case CollaborationVerificationResult.SelfAddNotAllowed:
        translatedText = 'SELF_ADD_NOT_ALLOWED';
        break;
      case CollaborationVerificationResult.Failure:
        translatedText = 'VERIFICATION_FAILURE';
        break;
      default:
      // sonar comply
    }
    return translatedText;
  }
}
